import {type ReactNode, type TouchEvent, useCallback, useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";

import {PropertyCarouselSize} from "../../types/PropertyCarouselSize";

const SCROLL_STEP_PX: Record<PropertyCarouselSize, number> = {
    small: 184,
    large: 200
};

interface IProps {
    children: ReactNode;
    itemCount: number;
    ariaLabel?: string;
    size?: PropertyCarouselSize;
    className?: string;
    previousButtonDataTestId?: string;
    nextButtonDataTestId?: string;
}

export const PropertyCarousel = (props: IProps) => {
    const {children, itemCount, ariaLabel, size = "small", className, previousButtonDataTestId, nextButtonDataTestId} = props;
    const listRef = useRef<HTMLUListElement>(null);
    const touchStartRef = useRef<{x: number; y: number} | null>(null);
    const [canScrollPrevious, setCanScrollPrevious] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const hasMultipleItems = itemCount > 1;

    const updateScrollButtons = useCallback(() => {
        const list = listRef.current;

        if (!list) {
            return;
        }

        setCanScrollPrevious(list.scrollLeft > 0);
        setCanScrollNext(list.scrollWidth > Math.ceil(list.clientWidth + list.scrollLeft));
    }, []);

    useEffect(() => {
        if (!hasMultipleItems) {
            setCanScrollPrevious(false);
            setCanScrollNext(false);
            return;
        }

        const list = listRef.current;

        if (!list) {
            return;
        }

        updateScrollButtons();
        list.addEventListener("scroll", updateScrollButtons);

        const resizeObserver = new ResizeObserver(updateScrollButtons);
        resizeObserver.observe(list);

        return () => {
            list.removeEventListener("scroll", updateScrollButtons);
            resizeObserver.disconnect();
        };
    }, [hasMultipleItems, itemCount, updateScrollButtons]);

    const scrollProperties = (direction: -1 | 1) => {
        listRef.current?.scrollBy({
            left: direction * SCROLL_STEP_PX[size],
            behavior: "smooth"
        });
    };

    const handleTouchStart = (event: TouchEvent<HTMLUListElement>) => {
        const touch = event.touches[0];
        touchStartRef.current = {x: touch.clientX, y: touch.clientY};
    };

    const handleTouchMove = (event: TouchEvent<HTMLUListElement>) => {
        const touchStart = touchStartRef.current;

        if (!touchStart || event.touches.length !== 1) {
            return;
        }

        const touch = event.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStart.x);
        const deltaY = Math.abs(touch.clientY - touchStart.y);

        if (deltaX > deltaY) {
            event.stopPropagation();
        }
    };

    const handleTouchEnd = () => {
        touchStartRef.current = null;
    };

    return (
        <div className={cx(carouselWrapperStyle, className)}>
            {hasMultipleItems && canScrollPrevious && (
                <button
                    type="button"
                    aria-label="Poprzednie mieszkania"
                    className={cx(carouselButtonStyle[size], carouselButtonPreviousStyle[size])}
                    onClick={() => scrollProperties(-1)}
                    data-testid={previousButtonDataTestId}
                >
                    <ChevronLeftIcon size="1.6" />
                </button>
            )}

            <ul
                ref={listRef}
                className={listStyle[size]}
                aria-label={ariaLabel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
            >
                {children}
            </ul>

            {hasMultipleItems && canScrollNext && (
                <button
                    type="button"
                    aria-label="Następne mieszkania"
                    className={cx(carouselButtonStyle[size], carouselButtonNextStyle[size])}
                    onClick={() => scrollProperties(1)}
                    data-testid={nextButtonDataTestId}
                >
                    <ChevronRightIcon size="1.6" />
                </button>
            )}
        </div>
    );
};

const carouselWrapperStyle = css({
    position: "relative",
    minWidth: 0
});

const listStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        listStyleType: "none",
        m: 0,
        p: 0,
        display: "flex",
        gap: 1.5,
        overflowX: "auto",
        overflowY: "hidden",
        scrollBehavior: "smooth",
        scrollSnapType: "x mandatory",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-x",
        overscrollBehaviorX: "contain",
        pr: 2,
        pb: 1,

        "&::-webkit-scrollbar": {
            display: "none"
        }
    }),
    large: css({
        listStyleType: "none",
        m: 0,
        mr: -2,
        p: 0,
        display: "flex",
        gap: 1,
        overflowX: "auto",
        overflowY: "hidden",
        scrollBehavior: "smooth",
        scrollSnapType: "x mandatory",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-x",
        overscrollBehaviorX: "contain",
        pr: 2,
        pb: 1,

        md: {
            mr: 0,
            pr: 0
        },

        "&::-webkit-scrollbar": {
            display: "none"
        }
    })
};

const carouselButtonBaseStyle = css.raw({
    position: "absolute",
    transform: "translateY(-50%)",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: 2,
    border: "1px solid var(--colors-gray-300)",
    backgroundColor: "var(--colors-white)",
    color: "var(--colors-secondary)",
    boxShadow: "var(--shadow-1)",
    cursor: "pointer",

    _hover: {
        backgroundColor: "var(--colors-gray-100)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    }
});

const carouselButtonStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        ...carouselButtonBaseStyle,
        top: "50%"
    }),
    large: css({
        ...carouselButtonBaseStyle,
        top: "7.85rem",
        display: "none",

        md: {
            display: "flex"
        }
    })
};

const carouselButtonPreviousStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        left: -1
    }),
    large: css({
        left: -2
    })
};

const carouselButtonNextStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        right: -1
    }),
    large: css({
        right: -2
    })
};
import {type ReactNode, type TouchEvent, useCallback, useEffect, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {ChevronLeftIcon, ChevronRightIcon} from "@pg-design/icons-module";

import {PropertyCarouselSize} from "../../types/PropertyCarouselSize";

const SCROLL_STEP_PX: Record<PropertyCarouselSize, number> = {
    small: 184,
    large: 200
};

interface IProps {
    children: ReactNode;
    itemCount: number;
    ariaLabel?: string;
    size?: PropertyCarouselSize;
    className?: string;
    previousButtonDataTestId?: string;
    nextButtonDataTestId?: string;
}

export const PropertyCarousel = (props: IProps) => {
    const {children, itemCount, ariaLabel, size = "small", className, previousButtonDataTestId, nextButtonDataTestId} = props;
    const listRef = useRef<HTMLUListElement>(null);
    const touchStartRef = useRef<{x: number; y: number} | null>(null);
    const [canScrollPrevious, setCanScrollPrevious] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const hasMultipleItems = itemCount > 1;

    const updateScrollButtons = useCallback(() => {
        const list = listRef.current;

        if (!list) {
            return;
        }

        setCanScrollPrevious(list.scrollLeft > 0);
        setCanScrollNext(list.scrollWidth > Math.ceil(list.clientWidth + list.scrollLeft));
    }, []);

    useEffect(() => {
        if (!hasMultipleItems) {
            setCanScrollPrevious(false);
            setCanScrollNext(false);
            return;
        }

        const list = listRef.current;

        if (!list) {
            return;
        }

        updateScrollButtons();
        list.addEventListener("scroll", updateScrollButtons);

        const resizeObserver = new ResizeObserver(updateScrollButtons);
        resizeObserver.observe(list);

        return () => {
            list.removeEventListener("scroll", updateScrollButtons);
            resizeObserver.disconnect();
        };
    }, [hasMultipleItems, itemCount, updateScrollButtons]);

    const scrollProperties = (direction: -1 | 1) => {
        listRef.current?.scrollBy({
            left: direction * SCROLL_STEP_PX[size],
            behavior: "smooth"
        });
    };

    const handleTouchStart = (event: TouchEvent<HTMLUListElement>) => {
        const touch = event.touches[0];
        touchStartRef.current = {x: touch.clientX, y: touch.clientY};
    };

    const handleTouchMove = (event: TouchEvent<HTMLUListElement>) => {
        const touchStart = touchStartRef.current;

        if (!touchStart || event.touches.length !== 1) {
            return;
        }

        const touch = event.touches[0];
        const deltaX = Math.abs(touch.clientX - touchStart.x);
        const deltaY = Math.abs(touch.clientY - touchStart.y);

        if (deltaX > deltaY) {
            event.stopPropagation();
        }
    };

    const handleTouchEnd = () => {
        touchStartRef.current = null;
    };

    return (
        <div className={cx(carouselWrapperStyle, className)}>
            {hasMultipleItems && canScrollPrevious && (
                <button
                    type="button"
                    aria-label="Poprzednie mieszkania"
                    className={cx(carouselButtonStyle[size], carouselButtonPreviousStyle[size])}
                    onClick={() => scrollProperties(-1)}
                    data-testid={previousButtonDataTestId}
                >
                    <ChevronLeftIcon size="1.6" />
                </button>
            )}

            <ul
                ref={listRef}
                className={listStyle[size]}
                aria-label={ariaLabel}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
            >
                {children}
            </ul>

            {hasMultipleItems && canScrollNext && (
                <button
                    type="button"
                    aria-label="Następne mieszkania"
                    className={cx(carouselButtonStyle[size], carouselButtonNextStyle[size])}
                    onClick={() => scrollProperties(1)}
                    data-testid={nextButtonDataTestId}
                >
                    <ChevronRightIcon size="1.6" />
                </button>
            )}
        </div>
    );
};

const carouselWrapperStyle = css({
    position: "relative",
    minWidth: 0
});

const listStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        listStyleType: "none",
        m: 0,
        p: 0,
        display: "flex",
        gap: 1.5,
        overflowX: "auto",
        overflowY: "hidden",
        scrollBehavior: "smooth",
        scrollSnapType: "x mandatory",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-x",
        overscrollBehaviorX: "contain",
        pr: 2,
        pb: 1,

        "&::-webkit-scrollbar": {
            display: "none"
        }
    }),
    large: css({
        listStyleType: "none",
        m: 0,
        mr: -2,
        p: 0,
        display: "flex",
        gap: 1,
        overflowX: "auto",
        overflowY: "hidden",
        scrollBehavior: "smooth",
        scrollSnapType: "x mandatory",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-x",
        overscrollBehaviorX: "contain",
        pr: 2,
        pb: 1,

        md: {
            mr: 0,
            pr: 0
        },

        "&::-webkit-scrollbar": {
            display: "none"
        }
    })
};

const carouselButtonBaseStyle = css.raw({
    position: "absolute",
    transform: "translateY(-50%)",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: 2,
    border: "1px solid var(--colors-gray-300)",
    backgroundColor: "var(--colors-white)",
    color: "var(--colors-secondary)",
    boxShadow: "var(--shadow-1)",
    cursor: "pointer",

    _hover: {
        backgroundColor: "var(--colors-gray-100)"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-secondary)",
        outlineOffset: "2px"
    }
});

const carouselButtonStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        ...carouselButtonBaseStyle,
        top: "50%"
    }),
    large: css({
        ...carouselButtonBaseStyle,
        top: "7.85rem",
        display: "none",

        md: {
            display: "flex"
        }
    })
};

const carouselButtonPreviousStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        left: -1
    }),
    large: css({
        left: -2
    })
};

const carouselButtonNextStyle: Record<PropertyCarouselSize, string> = {
    small: css({
        right: -1
    }),
    large: css({
        right: -2
    })
};
